import React, { useState } from 'react';
import { 
  X, 
  TrendingUp, 
  TrendingDown, 
  BarChart3, 
  Globe, 
  Users, 
  DollarSign, 
  Calendar, 
  Target, 
  AlertTriangle, 
  Lightbulb, 
  Activity,
  Zap,
  Shield,
  Brain,
  Clock,
  ExternalLink,
  Download,
  FlaskConical
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';
import CreateExperimentModal from './CreateExperimentModal';

interface MarketInsightDetailsModalProps {
  insight: any;
  isOpen: boolean;
  onClose: () => void;
}

const MarketInsightDetailsModal: React.FC<MarketInsightDetailsModalProps> = ({ insight, isOpen, onClose }) => {
  const [isCreateExperimentOpen, setIsCreateExperimentOpen] = useState(false);

  if (!isOpen || !insight) return null;

  // Market size and growth data
  const marketSizeData = [
    { year: '2020', size: 45.2, growth: 12.5, forecast: 45.2 },
    { year: '2021', size: 52.8, growth: 16.8, forecast: 50.7 },
    { year: '2022', size: 61.4, growth: 16.3, forecast: 56.8 },
    { year: '2023', size: 72.1, growth: 17.4, forecast: 63.6 },
    { year: '2024', size: 85.3, growth: 18.3, forecast: 71.2 },
    { year: '2025', size: null, growth: null, forecast: 79.8 },
    { year: '2026', size: null, growth: null, forecast: 89.5 },
    { year: '2027', size: null, growth: null, forecast: 100.2 },
  ];

  // Regional market distribution
  const regionalData = [
    { region: 'North America', share: 35, value: 29.9, growth: 15.2, color: '#4F46E5' },
    { region: 'Europe', share: 28, value: 23.9, growth: 12.8, color: '#10B981' },
    { region: 'Asia Pacific', share: 25, value: 21.3, growth: 22.4, color: '#F59E0B' },
    { region: 'Latin America', share: 8, value: 6.8, growth: 18.7, color: '#EF4444' },
    { region: 'Others', share: 4, value: 3.4, growth: 14.1, color: '#8B5CF6' },
  ];

  // Customer segment analysis
  const segmentData = [
    { segment: 'Enterprise', size: 42, growth: 14.5, needs: 'Scalability, Security, Integration' },
    { segment: 'SMB', size: 35, growth: 19.2, needs: 'Cost-effectiveness, Ease of use' },
    { segment: 'Startups', size: 23, growth: 28.7, needs: 'Innovation, Flexibility, Speed' },
  ];

  // Future needs and trends
  const futureNeeds = [
    {
      category: 'Technology',
      icon: Zap,
      needs: [
        'AI-powered automation and decision making',
        'Real-time data processing and analytics',
        'Cloud-native architecture and scalability',
        'Advanced security and privacy protection'
      ],
      priority: 'high',
      timeline: '6-12 months'
    },
    {
      category: 'User Experience',
      icon: Users,
      needs: [
        'Intuitive, mobile-first interfaces',
        'Personalised user experiences',
        'Voice and gesture-based interactions',
        'Accessibility and inclusive design'
      ],
      priority: 'high',
      timeline: '3-9 months'
    },
    {
      category: 'Business Model',
      icon: DollarSign,
      needs: [
        'Flexible pricing and subscription models',
        'Value-based pricing strategies',
        'Ecosystem and platform approaches',
        'Sustainability and ESG compliance'
      ],
      priority: 'medium',
      timeline: '12-18 months'
    },
    {
      category: 'Compliance',
      icon: Shield,
      needs: [
        'Enhanced data privacy regulations',
        'Industry-specific compliance requirements',
        'Cross-border data governance',
        'Audit and reporting capabilities'
      ],
      priority: 'high',
      timeline: '6-12 months'
    }
  ];

  // Competitive landscape
  const competitiveData = [
    { competitor: 'Market Leader A', share: 28, strength: 'Brand recognition', weakness: 'Innovation speed' },
    { competitor: 'Challenger B', share: 22, strength: 'Technology', weakness: 'Market reach' },
    { competitor: 'Our Position', share: 16, strength: 'Customer focus', weakness: 'Scale' },
    { competitor: 'Emerging C', share: 12, strength: 'Pricing', weakness: 'Reliability' },
    { competitor: 'Others', share: 22, strength: 'Niche focus', weakness: 'Resources' },
  ];

  // Risk and opportunity matrix
  const riskOpportunityData = [
    { name: 'Market Saturation', type: 'risk', impact: 'high', probability: 'medium', timeline: '12-18 months' },
    { name: 'Regulatory Changes', type: 'risk', impact: 'medium', probability: 'high', timeline: '6-12 months' },
    { name: 'AI Adoption Acceleration', type: 'opportunity', impact: 'high', probability: 'high', timeline: '3-6 months' },
    { name: 'New Market Segments', type: 'opportunity', impact: 'medium', probability: 'medium', timeline: '9-15 months' },
    { name: 'Technology Disruption', type: 'risk', impact: 'high', probability: 'low', timeline: '18-24 months' },
    { name: 'Partnership Opportunities', type: 'opportunity', impact: 'medium', probability: 'high', timeline: '6-9 months' },
  ];

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'opportunity': return 'text-green-400 bg-green-900 border-green-600';
      case 'growth': return 'text-blue-400 bg-blue-900 border-blue-600';
      case 'threat': return 'text-red-400 bg-red-900 border-red-600';
      default: return 'text-gray-400 bg-gray-900 border-gray-600';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'opportunity': return <Lightbulb className="w-5 h-5" />;
      case 'growth': return <TrendingUp className="w-5 h-5" />;
      case 'threat': return <AlertTriangle className="w-5 h-5" />;
      default: return <Activity className="w-5 h-5" />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'text-red-400 bg-red-900';
      case 'medium': return 'text-yellow-400 bg-yellow-900';
      case 'low': return 'text-green-400 bg-green-900';
      default: return 'text-gray-400 bg-gray-900';
    }
  };

  const getImpactColor = (impact: string) => {
    switch (impact) {
      case 'high': return 'text-red-400';
      case 'medium': return 'text-yellow-400';
      case 'low': return 'text-green-400';
      default: return 'text-gray-400';
    }
  };

  const handleCreateExperiment = (experimentData: any) => {
    // Here you would typically send the experiment data to your backend
    // and then navigate to the experiment hub or show a success message
    console.log('Creating experiment:', experimentData);
    
    // For now, we'll just show a success notification
    const notification = document.createElement('div');
    notification.className = 'fixed top-4 right-4 bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg z-50';
    notification.innerHTML = `
      <div class="flex items-center space-x-2">
        <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path>
        </svg>
        <span>Experiment "${experimentData.title}" created successfully!</span>
      </div>
    `;
    document.body.appendChild(notification);
    
    setTimeout(() => {
      document.body.removeChild(notification);
    }, 5000);
    
    setIsCreateExperimentOpen(false);
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-gray-800 rounded-lg max-w-7xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-700 bg-gray-900 rounded-t-lg">
            <div className="flex items-center space-x-4">
              <div className={`flex items-center space-x-2 px-3 py-2 rounded-lg border ${getTypeColor(insight.type)}`}>
                {getTypeIcon(insight.type)}
                <span className="capitalize font-medium">{insight.type}</span>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">{insight.title}</h2>
                <p className="text-gray-400">{insight.category} • {insight.lastUpdated}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg transition-colours flex items-center space-x-2">
                <Download className="w-4 h-4" />
                <span>Export Report</span>
              </button>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-white transition-colours p-2 rounded-lg hover:bg-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 space-y-8">
            {/* Executive Summary */}
            <div className="bg-gray-700 rounded-lg p-6">
              <h3 className="text-xl font-semibold text-white mb-4 flex items-center space-x-2">
                <Target className="w-6 h-6 text-indigo-400" />
                <span>Executive Summary</span>
              </h3>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2">
                  <p className="text-gray-300 leading-relaxed mb-4">{insight.description}</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center">
                      <div className="text-sm text-gray-400">Confidence</div>
                      <div className="text-2xl font-bold text-white">{insight.confidence}%</div>
                    </div>
                    <div className="text-center">
                      <div className="text-sm text-gray-400">Impact</div>
                      <div className={`text-xl font-bold capitalize ${getImpactColor(insight.impact)}`}>
                        {insight.impact}
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-sm text-gray-400">Timeline</div>
                      <div className="text-lg font-bold text-white">{insight.timeframe}</div>
                    </div>
                    <div className="text-center">
                      <div className="text-sm text-gray-400">Investment</div>
                      <div className="text-lg font-bold text-green-400">{insight.investment}</div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-800 rounded-lg p-4">
                  <h4 className="text-lg font-semibold text-white mb-3">Key Metrics</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Market Size (2024)</span>
                      <span className="text-white font-bold">£85.3B</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Growth Rate</span>
                      <span className="text-green-400 font-bold">+18.3%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Our Market Share</span>
                      <span className="text-indigo-400 font-bold">16%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">ROI Potential</span>
                      <span className="text-yellow-400 font-bold">{insight.roi}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Market Size Analysis */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-gray-700 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
                  <BarChart3 className="w-5 h-5 text-green-400" />
                  <span>Market Size & Growth Forecast</span>
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={marketSizeData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis dataKey="year" stroke="#9CA3AF" />
                    <YAxis stroke="#9CA3AF" />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#1F2937',
                        border: '1px solid #374151',
                        borderRadius: '8px',
                        color: '#F9FAFB'
                      }}
                    />
                    <Area type="monotone" dataKey="size" stroke="#10B981" fill="#10B981" fillOpacity={0.6} name="Actual Size (£B)" />
                    <Area type="monotone" dataKey="forecast" stroke="#3B82F6" fill="#3B82F6" fillOpacity={0.3} strokeDasharray="5 5" name="Forecast (£B)" />
                  </AreaChart>
                </ResponsiveContainer>
                <div className="mt-4 grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-sm text-gray-400">2024 Size</div>
                    <div className="text-xl font-bold text-green-400">£85.3B</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-400">2027 Forecast</div>
                    <div className="text-xl font-bold text-blue-400">£100.2B</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-400">CAGR</div>
                    <div className="text-xl font-bold text-purple-400">17.2%</div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-700 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
                  <Globe className="w-5 h-5 text-blue-400" />
                  <span>Regional Market Distribution</span>
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={regionalData}
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      dataKey="share"
                      label={({ region, share }) => `${region}: ${share}%`}
                      labelLine={false}
                    >
                      {regionalData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#1F2937',
                        border: '1px solid #374151',
                        borderRadius: '8px',
                        color: '#F9FAFB'
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Customer Segments & Future Needs */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-gray-700 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
                  <Users className="w-5 h-5 text-purple-400" />
                  <span>Customer Segment Analysis</span>
                </h3>
                <div className="space-y-4">
                  {segmentData.map((segment, index) => (
                    <div key={index} className="bg-gray-800 rounded-lg p-4">
                      <div className="flex justify-between items-center mb-2">
                        <h4 className="text-white font-medium">{segment.segment}</h4>
                        <div className="flex items-center space-x-4">
                          <span className="text-sm text-gray-400">Size: {segment.size}%</span>
                          <span className="text-sm text-green-400">Growth: +{segment.growth}%</span>
                        </div>
                      </div>
                      <p className="text-sm text-gray-400">{segment.needs}</p>
                      <div className="mt-2 w-full bg-gray-600 rounded-full h-2">
                        <div 
                          className="bg-purple-500 h-2 rounded-full" 
                          style={{ width: `${segment.size}%` }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-gray-700 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
                  <Brain className="w-5 h-5 text-cyan-400" />
                  <span>Future Market Needs</span>
                </h3>
                <div className="space-y-4">
                  {futureNeeds.map((need, index) => (
                    <div key={index} className="bg-gray-800 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-2">
                          <need.icon className="w-4 h-4 text-cyan-400" />
                          <h4 className="text-white font-medium">{need.category}</h4>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className={`px-2 py-1 rounded-full text-xs ${getPriorityColor(need.priority)}`}>
                            {need.priority} priority
                          </span>
                          <span className="text-xs text-gray-400">{need.timeline}</span>
                        </div>
                      </div>
                      <ul className="space-y-1">
                        {need.needs.slice(0, 2).map((item, itemIndex) => (
                          <li key={itemIndex} className="text-sm text-gray-400 flex items-center space-x-2">
                            <div className="w-1 h-1 bg-cyan-400 rounded-full"></div>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Competitive Landscape */}
            <div className="bg-gray-700 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
                <Target className="w-5 h-5 text-orange-400" />
                <span>Competitive Landscape</span>
              </h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={competitiveData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="competitor" stroke="#9CA3AF" />
                  <YAxis stroke="#9CA3AF" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1F2937',
                      border: '1px solid #374151',
                      borderRadius: '8px',
                      color: '#F9FAFB'
                    }}
                  />
                  <Bar dataKey="share" fill="#F59E0B" />
                </BarChart>
              </ResponsiveContainer>
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {competitiveData.slice(0, 3).map((comp, index) => (
                  <div key={index} className="bg-gray-800 rounded-lg p-3">
                    <h4 className="text-white font-medium mb-2">{comp.competitor}</h4>
                    <div className="text-sm space-y-1">
                      <div className="flex justify-between">
                        <span className="text-gray-400">Market Share:</span>
                        <span className="text-white">{comp.share}%</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Strength:</span>
                        <span className="text-green-400">{comp.strength}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Weakness:</span>
                        <span className="text-red-400">{comp.weakness}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Risk & Opportunity Matrix */}
            <div className="bg-gray-700 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
                <Activity className="w-5 h-5 text-red-400" />
                <span>Risk & Opportunity Matrix</span>
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-white font-medium mb-3 flex items-center space-x-2">
                    <AlertTriangle className="w-4 h-4 text-red-400" />
                    <span>Key Risks</span>
                  </h4>
                  <div className="space-y-3">
                    {riskOpportunityData.filter(item => item.type === 'risk').map((risk, index) => (
                      <div key={index} className="bg-gray-800 rounded-lg p-3">
                        <div className="flex justify-between items-center mb-2">
                          <h5 className="text-white font-medium">{risk.name}</h5>
                          <span className="text-xs text-gray-400">{risk.timeline}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className={`${getImpactColor(risk.impact)}`}>
                            Impact: {risk.impact}
                          </span>
                          <span className={`${getImpactColor(risk.probability)}`}>
                            Probability: {risk.probability}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <h4 className="text-white font-medium mb-3 flex items-center space-x-2">
                    <Lightbulb className="w-4 h-4 text-green-400" />
                    <span>Key Opportunities</span>
                  </h4>
                  <div className="space-y-3">
                    {riskOpportunityData.filter(item => item.type === 'opportunity').map((opp, index) => (
                      <div key={index} className="bg-gray-800 rounded-lg p-3">
                        <div className="flex justify-between items-center mb-2">
                          <h5 className="text-white font-medium">{opp.name}</h5>
                          <span className="text-xs text-gray-400">{opp.timeline}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className={`${getImpactColor(opp.impact)}`}>
                            Impact: {opp.impact}
                          </span>
                          <span className={`${getImpactColor(opp.probability)}`}>
                            Probability: {opp.probability}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Strategic Recommendations */}
            <div className="bg-gray-700 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
                <Target className="w-5 h-5 text-indigo-400" />
                <span>Strategic Recommendations</span>
              </h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-white font-medium mb-3">Immediate Actions (0-6 months)</h4>
                  <div className="space-y-2">
                    {insight.recommendations.slice(0, 2).map((rec: string, index: number) => (
                      <div key={index} className="flex items-start space-x-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-gray-300 text-sm">{rec}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <h4 className="text-white font-medium mb-3">Medium-term Strategy (6-18 months)</h4>
                  <div className="space-y-2">
                    {insight.recommendations.slice(2).map((rec: string, index: number) => (
                      <div key={index} className="flex items-start space-x-2">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-gray-300 text-sm">{rec}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Action Items */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-3 sm:space-y-0 pt-6 border-t border-gray-700">
              <div className="flex flex-wrap items-center gap-4 text-sm text-gray-400">
                <span>Last updated: {insight.lastUpdated}</span>
                <span>•</span>
                <span>Next review: {new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toLocaleDateString()}</span>
                <span>•</span>
                <span>Confidence: {insight.confidence}%</span>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <button 
                  onClick={() => setIsCreateExperimentOpen(true)}
                  className="bg-gray-600 hover:bg-gray-500 text-white px-4 py-2 rounded-lg transition-colours flex items-center space-x-2"
                >
                  <FlaskConical className="w-4 h-4" />
                  <span>Create Experiment</span>
                </button>
                <button className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg transition-colours">
                  Add to Backlog
                </button>
                <button className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colours">
                  Approve Strategy
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Create Experiment Modal */}
      <CreateExperimentModal
        insight={insight}
        isOpen={isCreateExperimentOpen}
        onClose={() => setIsCreateExperimentOpen(false)}
        onCreateExperiment={handleCreateExperiment}
      />
    </>
  );
};

export default MarketInsightDetailsModal;